import { useState, useEffect } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import translations from "@/constants/translations";
import { MENU_PAGE } from "@/constants/testIds";
import { UtensilsCrossed } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const getImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}/api/files/${path}`;
};

export default function MenuPage() {
  const [settings, setSettings] = useState({});
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();
  const t = translations[lang];

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/settings`),
      axios.get(`${API}/categories`),
      axios.get(`${API}/dishes`),
    ]).then(([s, c, d]) => {
      setSettings(s.data || {});
      const cats = c.data || [];
      setCategories(cats);
      setDishes(d.data || []);
      if (cats.length > 0) setActiveCategory(cats[0].id);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const getDishesForCategory = (catId) =>
    dishes.filter(d => d.category_id === catId && d.available);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0077B6] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen font-lato bg-white" data-testid={MENU_PAGE.page}>
      <Header settings={settings} />

      {/* Page Header */}
      <div className="pt-16 md:pt-20 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 text-center">
          <span className="font-dancing text-[#0077B6] text-xl block mb-2">
            {t.menu.badge}
          </span>
          <h1 className="font-cormorant text-5xl md:text-6xl font-light text-white">
            {t.menu.title}
          </h1>
          <div className="w-12 h-0.5 bg-[#C00000] mx-auto mt-4" />
          <p className="font-lato text-gray-400 mt-4 text-sm tracking-wider">
            {t.menu.subtitle}
          </p>
        </div>
      </div>

      {/* Category tabs */}
      <div className="sticky top-16 md:top-20 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-0 overflow-x-auto scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat.id}
                data-testid={MENU_PAGE.categoryTab}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex-shrink-0 px-6 py-4 font-lato text-xs tracking-widest uppercase transition-all duration-200 border-b-2 ${
                  activeCategory === cat.id
                    ? "border-[#0077B6] text-[#0077B6]"
                    : "border-transparent text-[#4A4A4A] hover:text-[#0077B6]"
                }`}
              >
                {lang === "pt" ? cat.name_pt : cat.name_en}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Dishes */}
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        {categories.map(cat => {
          if (activeCategory !== cat.id) return null;
          const catDishes = getDishesForCategory(cat.id);

          return (
            <div key={cat.id}>
              <div className="mb-10">
                <h2 className="font-cormorant text-4xl text-[#4A4A4A]">
                  {lang === "pt" ? cat.name_pt : cat.name_en}
                </h2>
                <div className="w-10 h-0.5 bg-[#C00000] mt-2" />
              </div>

              {catDishes.length === 0 ? (
                <div className="py-16 text-center text-gray-400">
                  <UtensilsCrossed size={36} className="mx-auto mb-3" />
                  <p className="font-lato text-sm">Sem pratos disponíveis nesta categoria</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {catDishes.map(dish => (
                    <div
                      key={dish.id}
                      data-testid={MENU_PAGE.dishCard}
                      className="group bg-white border border-gray-100 hover:border-[#0077B6]/30 hover:shadow-lg transition-all duration-300"
                    >
                      {/* Image */}
                      <div className="overflow-hidden h-48">
                        {dish.image_path ? (
                          <img
                            src={getImageUrl(dish.image_path)}
                            alt={lang === "pt" ? dish.name_pt : dish.name_en}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-50 flex flex-col items-center justify-center gap-1 text-gray-300">
                            <UtensilsCrossed size={30} />
                            <span className="font-lato text-xs">{t.menu.noImage}</span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3
                            data-testid={MENU_PAGE.dishName}
                            className="font-cormorant text-xl text-[#4A4A4A] font-medium leading-tight"
                          >
                            {lang === "pt" ? dish.name_pt : dish.name_en}
                          </h3>
                          <span
                            data-testid={MENU_PAGE.dishPrice}
                            className="font-cormorant text-xl text-[#C00000] font-semibold flex-shrink-0"
                          >
                            {dish.price.toFixed(2)}€
                          </span>
                        </div>
                        {(dish.description_pt || dish.description_en) && (
                          <p
                            data-testid={MENU_PAGE.dishDescription}
                            className="font-lato text-gray-500 text-sm leading-relaxed"
                          >
                            {lang === "pt" ? dish.description_pt : dish.description_en}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Reserve CTA */}
      <div className="bg-[#F9FAFB] py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <span className="font-dancing text-[#0077B6] text-xl block mb-2">
            — {lang === "pt" ? "Reserve a sua mesa" : "Reserve your table"} —
          </span>
          <h3 className="font-cormorant text-3xl md:text-4xl text-[#4A4A4A] mb-6">
            {lang === "pt" ? "Uma experiência gastronómica inesquecível" : "An unforgettable gastronomic experience"}
          </h3>
          <a
            href={settings.reservation_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-10 py-4 bg-[#0077B6] text-white font-lato text-sm tracking-[0.2em] uppercase hover:bg-[#005f8f] transition-colors duration-200"
          >
            {t.nav.reserve}
          </a>
        </div>
      </div>

      <Footer settings={settings} />
    </div>
  );
}
